import {
  clientCommandEnvelopeSchema,
  createRoomResponseSchema,
  GAME_WEBSOCKET_PROTOCOL,
  gameTakebackResponseSchema,
  gameTimingMovesResponseSchema,
  gameTimingReportResponseSchema,
  getGameResponseSchema,
  joinRoomResponseSchema,
  playerGameHistoryResponseSchema,
  playerCurrentGamesResponseSchema,
  gamePlayersPresenceResponseSchema,
  playerRecoverableGameResponseSchema,
  serverEventEnvelopeSchema,
  WEBSOCKET_JWT_PROTOCOL_PREFIX,
  type BoardPoint,
  type CreatorColorPreference,
  type GameType,
  type NigiriParity,
  type ClientCommandEnvelope,
  type GameTakebackSnapshot,
  type GameTimingMove,
  type GameTimingReportResponse,
  type PlayerGameHistoryResponse,
  type ServerEventEnvelope,
  type TimedGameSnapshot,
} from '@wx-weiqi/contracts';

import { nextRequestId, WebApiError } from '../services/http';
import { localizeGameError } from '../domain/game-error';
import { assertCompleteTimingMoves } from '../domain/timing-report';
import type { IdentityStore } from './identity-store';

export type ConnectionStatus = 'idle' | 'connecting' | 'online' | 'reconnecting' | 'offline';

export interface GameState {
  readonly snapshot: TimedGameSnapshot | null;
  readonly connection: ConnectionStatus;
  readonly pendingCommand: string | null;
  readonly error: string | null;
  readonly activeTakeback: GameTakebackSnapshot | null;
  readonly lastMove: BoardPoint | null;
  readonly report: GameTimingReportResponse['data'] | null;
  readonly reportMoves: readonly GameTimingMove[];
  readonly reportPending: boolean;
  readonly reportPendingGameId: string | null;
  readonly history: PlayerGameHistoryResponse['data'] | null;
  readonly historyPending: boolean;
  readonly downloadPendingGameId: string | null;
}

type Listener = () => void;

export type VersionDecision = 'ignore' | 'apply' | 'sync';

function errorMessage(error: unknown, fallback: string): string {
  return localizeGameError(
    error instanceof WebApiError ? error.code : undefined,
    error instanceof Error ? error.message : undefined,
    fallback,
  );
}

export function decideServerVersion(
  currentVersion: number,
  event: ServerEventEnvelope,
): VersionDecision {
  if (event.event === 'game.state.sync') {
    return event.stateVersion < currentVersion ? 'ignore' : 'apply';
  }
  if (event.event === 'game.state.delta') {
    if (event.payload.fromStateVersion !== currentVersion) {
      return event.stateVersion <= currentVersion ? 'ignore' : 'sync';
    }
    return 'apply';
  }
  if (event.event === 'command.rejected' || event.event === 'game.move.rejected') return 'ignore';
  if (event.stateVersion <= currentVersion) return 'ignore';
  return event.stateVersion === currentVersion + 1 ? 'apply' : 'sync';
}

export class GameStore {
  private stateValue: GameState = {
    snapshot: null,
    connection: 'idle',
    pendingCommand: null,
    error: null,
    activeTakeback: null,
    lastMove: null,
    report: null,
    reportMoves: [],
    reportPending: false,
    reportPendingGameId: null,
    history: null,
    historyPending: false,
    downloadPendingGameId: null,
  };
  private readonly listeners = new Set<Listener>();
  private readonly seenEventIds = new Set<string>();
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private recoverySyncTimer: ReturnType<typeof setTimeout> | null = null;
  private recoverySyncAttempt = 0;
  private disposed = false;

  constructor(
    private readonly identity: IdentityStore,
    private readonly websocketUrl: string,
  ) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): GameState => this.stateValue;

  async createRoom(input: {
    readonly boardSize: 9 | 13 | 19;
    readonly mainTimeMs: number;
    readonly byoyomiPeriodTimeMs: 10_000 | 15_000 | 30_000 | 60_000 | null;
    readonly byoyomiPeriods: 1 | 2 | 3 | 5 | 10;
    readonly gameType: GameType;
    readonly creatorColor: CreatorColorPreference;
  }): Promise<void> {
    const response = await this.identity.authorized('/games/rooms', createRoomResponseSchema, {
      method: 'POST',
      requestId: nextRequestId('web-room-create'),
      body: {
        boardSize: input.boardSize,
        timing: {
          ruleCode: 'ABSOLUTE_V1',
          mainTimeMs: input.mainTimeMs,
          ...(input.byoyomiPeriodTimeMs === null
            ? {}
            : {
                byoyomi: {
                  periodTimeMs: input.byoyomiPeriodTimeMs,
                  periods: input.byoyomiPeriods,
                },
              }),
        },
        gameType: input.gameType,
        creatorColor: input.creatorColor,
      },
    });
    this.open(response.data);
  }

  async joinRoom(roomCode: string): Promise<void> {
    const response = await this.identity.authorized(
      `/games/rooms/code/${encodeURIComponent(roomCode.trim().toUpperCase())}/join`,
      joinRoomResponseSchema,
      {
        method: 'POST',
        requestId: nextRequestId('web-room-join'),
        body: {},
      },
    );
    this.open(response.data);
  }

  async loadGame(gameId: string): Promise<void> {
    const response = await this.identity.authorized(
      `/games/${encodeURIComponent(gameId.trim())}`,
      getGameResponseSchema,
    );
    this.open(response.data);
  }

  async listCurrentGames(after: string | null = null) {
    const response = await this.identity.authorized(
      `/me/games/current?limit=20${after === null ? '' : `&after=${encodeURIComponent(after)}`}`,
      playerCurrentGamesResponseSchema,
    );
    return response.data;
  }

  async loadPlayersPresence(gameId: string) {
    const response = await this.identity.authorized(
      `/games/${encodeURIComponent(gameId)}/players-presence`,
      gamePlayersPresenceResponseSchema,
    );
    return response.data.onlineMembershipIds;
  }

  async recoverLatestGame(): Promise<void> {
    try {
      const response = await this.identity.authorized(
        '/me/games/recoverable',
        playerRecoverableGameResponseSchema,
      );
      if (response.data.game === null) {
        this.update({
          ...this.stateValue,
          error: '暂无可恢复的进行中对局。',
        });
        return;
      }
      await this.loadGame(response.data.game.gameId);
    } catch (error: unknown) {
      this.update({
        ...this.stateValue,
        error: errorMessage(error, '恢复对局失败，请稍后重试。'),
      });
      throw error;
    }
  }

  open(snapshot: TimedGameSnapshot): void {
    this.leave();
    this.disposed = false;
    this.seenEventIds.clear();
    this.update({
      snapshot,
      connection: 'connecting',
      pendingCommand: null,
      error: null,
      activeTakeback: null,
      lastMove: null,
      report: null,
      reportMoves: [],
      reportPending: false,
      reportPendingGameId: null,
      history: this.stateValue.history,
      historyPending: false,
      downloadPendingGameId: null,
    });
    void this.connect();
    void this.refreshActiveTakeback();
  }

  leave(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const socket = this.socket;
    this.socket = null;
    if (socket !== null && socket.readyState < WebSocket.CLOSING) socket.close(1000, 'game closed');
    this.update({
      snapshot: null,
      connection: 'idle',
      pendingCommand: null,
      error: null,
      activeTakeback: null,
      lastMove: null,
      report: null,
      reportMoves: [],
      reportPending: false,
      reportPendingGameId: null,
      history: this.stateValue.history,
      historyPending: false,
      downloadPendingGameId: null,
    });
  }

  clear(): void {
    this.leave();
    if (this.recoverySyncTimer !== null) clearTimeout(this.recoverySyncTimer);
    this.recoverySyncTimer = null;
    this.recoverySyncAttempt = 0;
    this.seenEventIds.clear();
    this.update({
      snapshot: null,
      connection: 'idle',
      pendingCommand: null,
      error: null,
      activeTakeback: null,
      lastMove: null,
      report: null,
      reportMoves: [],
      reportPending: false,
      reportPendingGameId: null,
      history: null,
      historyPending: false,
      downloadPendingGameId: null,
    });
  }

  ready(): void {
    this.send('room.ready', {});
  }

  selectNigiri(number: number): void {
    this.send('game.nigiri.select', { number });
  }

  guessNigiri(parity: NigiriParity): void {
    this.send('game.nigiri.guess', { parity });
  }

  play(point: BoardPoint): void {
    this.send('game.move', point);
  }

  pass(): void {
    this.send('game.pass', {});
  }

  resign(): void {
    this.send('game.resign', {});
  }

  requestCounting(): void {
    this.send('game.counting.request', {});
  }

  respondCounting(countingRequestId: string, accepted: boolean): void {
    this.send('game.counting.respond', { countingRequestId, accepted });
  }

  proposeScoring(deadStoneAnchors: readonly BoardPoint[]): void {
    this.send('game.scoring.propose', { deadStoneAnchors: [...deadStoneAnchors] });
  }

  confirmScoring(scoringVersion: number, scoringHash: string): void {
    this.send('game.scoring.confirm', { scoringVersion, scoringHash });
  }

  rejectScoring(scoringVersion: number, scoringHash: string): void {
    this.send('game.scoring.reject', { scoringVersion, scoringHash });
  }

  resumeScoring(
    scoringVersion: number,
    scoringHash: string,
    disputedGroupAnchor: BoardPoint,
  ): void {
    this.send('game.scoring.resume', { scoringVersion, scoringHash, disputedGroupAnchor });
  }

  async requestTakeback(): Promise<void> {
    if (this.stateValue.pendingCommand !== null) return;
    const snapshot = this.requireSnapshot();
    this.update({ ...this.stateValue, pendingCommand: '申请悔棋', error: null });
    try {
      const response = await this.identity.authorized(
        `/games/${encodeURIComponent(snapshot.gameId)}/takebacks`,
        gameTakebackResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-takeback'),
          body: { expectedGameStateVersion: snapshot.stateVersion },
        },
      );
      this.acceptRestSnapshot(response.data.snapshot, response.data.takeback);
    } catch (error: unknown) {
      this.commandFailed(error);
    }
  }

  async respondTakeback(accepted: boolean): Promise<void> {
    if (this.stateValue.pendingCommand !== null) return;
    const snapshot = this.requireSnapshot();
    const takeback = this.stateValue.activeTakeback;
    if (takeback === null) return;
    this.update({
      ...this.stateValue,
      pendingCommand: accepted ? '同意悔棋' : '拒绝悔棋',
      error: null,
    });
    try {
      const response = await this.identity.authorized(
        `/games/${encodeURIComponent(snapshot.gameId)}/takebacks/${encodeURIComponent(takeback.takebackId)}/respond`,
        gameTakebackResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-takeback-response'),
          body: {
            expectedGameStateVersion: snapshot.stateVersion,
            expectedTakebackVersion: takeback.takebackVersion,
            accepted,
          },
        },
      );
      this.acceptRestSnapshot(response.data.snapshot, response.data.takeback);
    } catch (error: unknown) {
      this.commandFailed(error);
    }
  }

  async loadTimingReport(gameId = this.requireSnapshot().gameId): Promise<void> {
    this.update({
      ...this.stateValue,
      reportPending: true,
      reportPendingGameId: gameId,
      error: null,
    });
    try {
      const reportResponse = await this.identity.authorized(
        `/games/${encodeURIComponent(gameId)}/timing/report`,
        gameTimingReportResponseSchema,
      );
      const moves: GameTimingMove[] = [];
      let afterSequence = 0;
      while (moves.length < reportResponse.data.moveCount) {
        const moveResponse = await this.identity.authorized(
          `${reportResponse.data.movesPath}?afterSequence=${afterSequence}&limit=100`,
          gameTimingMovesResponseSchema,
        );
        if (moveResponse.gameId !== gameId) {
          throw new Error('逐手用时响应不属于当前对局。');
        }
        moves.push(...moveResponse.data.moves);
        if (moveResponse.data.nextAfterSequence === null) break;
        if (moveResponse.data.nextAfterSequence <= afterSequence) {
          throw new Error('逐手用时分页游标未向前推进，请稍后重试。');
        }
        afterSequence = moveResponse.data.nextAfterSequence;
      }
      const completeMoves = assertCompleteTimingMoves(moves, reportResponse.data.moveCount);
      if (this.stateValue.reportPendingGameId !== gameId) return;
      this.update({
        ...this.stateValue,
        report: reportResponse.data,
        reportMoves: completeMoves,
        reportPending: false,
        reportPendingGameId: null,
        error: null,
      });
    } catch (error: unknown) {
      if (this.stateValue.reportPendingGameId !== gameId) return;
      this.update({
        ...this.stateValue,
        reportPending: false,
        reportPendingGameId: null,
        error: errorMessage(error, '用时报告加载失败。'),
      });
    }
  }

  closeTimingReport(): void {
    this.update({
      ...this.stateValue,
      report: null,
      reportMoves: [],
      reportPending: false,
      reportPendingGameId: null,
    });
  }

  async loadHistory(): Promise<void> {
    this.update({ ...this.stateValue, historyPending: true, error: null });
    try {
      const games: PlayerGameHistoryResponse['data']['games'][number][] = [];
      const seenCursors = new Set<string>();
      let after: string | null = null;
      while (true) {
        const response: PlayerGameHistoryResponse = await this.identity.authorized(
          `/me/games/history?limit=50${after === null ? '' : `&after=${encodeURIComponent(after)}`}`,
          playerGameHistoryResponseSchema,
        );
        games.push(...response.data.games);
        const next: string | null = response.data.nextCursor;
        if (next === null) break;
        if (seenCursors.has(next)) throw new Error('历史棋谱分页游标没有向前推进。');
        seenCursors.add(next);
        after = next;
      }
      this.update({
        ...this.stateValue,
        history: { games, nextCursor: null },
        historyPending: false,
        error: null,
      });
    } catch (error: unknown) {
      this.update({
        ...this.stateValue,
        historyPending: false,
        error: errorMessage(error, '历史棋谱加载失败。'),
      });
    }
  }

  async downloadSgf(gameId = this.requireSnapshot().gameId): Promise<void> {
    this.update({ ...this.stateValue, downloadPendingGameId: gameId, error: null });
    try {
      const file = await this.identity.authorizedDownload(
        `/games/${encodeURIComponent(gameId)}/record.sgf`,
        `wx-weiqi-${gameId}.sgf`,
      );
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.filename;
      anchor.hidden = true;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      this.update({ ...this.stateValue, downloadPendingGameId: null, error: null });
    } catch (error: unknown) {
      this.update({
        ...this.stateValue,
        downloadPendingGameId: null,
        error: errorMessage(error, 'SGF 棋谱下载失败。'),
      });
    }
  }

  clearError(): void {
    this.update({ ...this.stateValue, error: null });
  }

  private async connect(forceRefresh = false): Promise<void> {
    const snapshot = this.stateValue.snapshot;
    if (snapshot === null || this.disposed) return;
    this.update({
      ...this.stateValue,
      connection: this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting',
    });

    try {
      const token = forceRefresh
        ? await this.identity.renewAccessToken()
        : await this.identity.ensureAccessToken();
      if (this.disposed) return;
      const socket = new WebSocket(this.websocketUrl, [
        GAME_WEBSOCKET_PROTOCOL,
        `${WEBSOCKET_JWT_PROTOCOL_PREFIX}${token}`,
      ]);
      this.socket = socket;
      socket.addEventListener('open', () => {
        if (socket !== this.socket) return;
        const reconnected = this.reconnectAttempt > 0;
        this.reconnectAttempt = 0;
        this.update({ ...this.stateValue, connection: 'online', error: null });
        this.send('room.join', {}, false);
        if (reconnected) void this.refreshActiveTakeback();
      });
      socket.addEventListener('message', (message) => this.receive(message.data));
      socket.addEventListener('close', (event) => {
        if (socket !== this.socket) return;
        this.socket = null;
        if (this.disposed) return;
        if (event.code === 4403) {
          this.update({ ...this.stateValue, connection: 'offline', error: '当前机构身份已停用。' });
          return;
        }
        this.scheduleReconnect(event.code === 4401);
      });
      socket.addEventListener('error', () => {
        if (socket === this.socket) socket.close();
      });
    } catch (error: unknown) {
      this.update({
        ...this.stateValue,
        error: errorMessage(error, '实时连接失败。'),
      });
      this.scheduleReconnect(false);
    }
  }

  private scheduleReconnect(refreshToken: boolean): void {
    if (this.disposed || this.stateValue.snapshot === null || this.reconnectTimer !== null) return;
    this.reconnectAttempt += 1;
    this.update({ ...this.stateValue, connection: 'reconnecting' });
    const delay = Math.min(10_000, 500 * 2 ** Math.min(this.reconnectAttempt - 1, 5));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect(refreshToken);
    }, delay);
  }

  private send<Event extends ClientCommandEnvelope['event']>(
    event: Event,
    payload: Extract<ClientCommandEnvelope, { event: Event }>['payload'],
    showPending = true,
  ): void {
    // Synchronous guard also covers two activations before React paints the pending state.
    // Recovery synchronization remains available while a command is awaiting acknowledgment.
    if (showPending && this.stateValue.pendingCommand !== null) return;
    const snapshot = this.requireSnapshot();
    const requestId = nextRequestId('web-game');
    const parsed = clientCommandEnvelopeSchema.parse({
      event,
      requestId,
      gameId: snapshot.gameId,
      stateVersion: snapshot.stateVersion,
      payload,
    });
    if (this.socket?.readyState !== WebSocket.OPEN) {
      this.update({ ...this.stateValue, error: '实时连接尚未恢复，请稍候。' });
      return;
    }
    this.socket.send(JSON.stringify(parsed));
    if (showPending) {
      this.update({ ...this.stateValue, pendingCommand: requestId, error: null });
    }
  }

  private receive(value: unknown): void {
    let decoded: unknown;
    try {
      decoded = typeof value === 'string' ? JSON.parse(value) : JSON.parse(String(value));
    } catch {
      this.update({ ...this.stateValue, error: '收到无法解析的实时消息。' });
      return;
    }
    const parsed = serverEventEnvelopeSchema.safeParse(decoded);
    if (!parsed.success) {
      this.update({ ...this.stateValue, error: '实时消息未通过契约校验。' });
      return;
    }
    this.applyEvent(parsed.data);
  }

  private applyEvent(event: ServerEventEnvelope): void {
    if (this.seenEventIds.has(event.eventId)) return;
    this.seenEventIds.add(event.eventId);
    if (this.seenEventIds.size > 512) {
      const oldest = this.seenEventIds.values().next().value as string | undefined;
      if (oldest !== undefined) this.seenEventIds.delete(oldest);
    }

    const snapshot = this.stateValue.snapshot;
    if (snapshot === null || event.gameId !== snapshot.gameId) return;
    if (event.requestId === this.stateValue.pendingCommand) {
      this.update({ ...this.stateValue, pendingCommand: null });
    }
    if (event.event === 'command.rejected' || event.event === 'game.move.rejected') {
      this.update({
        ...this.stateValue,
        pendingCommand: null,
        error: localizeGameError(event.payload.code, event.payload.message),
      });
      if (event.payload.syncRequired === true) this.requestSync();
      return;
    }

    const decision = decideServerVersion(snapshot.stateVersion, event);
    if (decision === 'ignore') return;
    if (decision === 'sync') {
      this.requestSync();
      return;
    }

    const nextSnapshot = event.payload.snapshot;
    let takeback =
      event.event === 'game.takeback.requested' || event.event === 'game.takeback.resolved'
        ? event.payload.takeback
        : this.stateValue.activeTakeback;
    let lastMove =
      event.event === 'game.move.accepted' && event.payload.move.point !== null
        ? event.payload.move.point
        : this.stateValue.lastMove;

    if (event.event === 'game.state.sync') lastMove = null;
    if (event.event === 'game.takeback.resolved') lastMove = null;
    if (event.event === 'game.state.delta') {
      for (const replayed of [...event.payload.events].reverse()) {
        if (
          replayed.event === 'game.takeback.requested' ||
          replayed.event === 'game.takeback.resolved'
        ) {
          takeback = replayed.payload.takeback;
          if (replayed.event === 'game.takeback.resolved') lastMove = null;
          break;
        }
      }
      for (const replayed of [...event.payload.events].reverse()) {
        if (replayed.event === 'game.takeback.resolved') {
          lastMove = null;
          break;
        }
        if (replayed.event === 'game.move.accepted' && replayed.payload.move.point !== null) {
          lastMove = replayed.payload.move.point;
          break;
        }
      }
    }
    this.update({
      ...this.stateValue,
      snapshot: nextSnapshot,
      activeTakeback: takeback?.status === 'PENDING' ? takeback : null,
      lastMove,
      pendingCommand:
        event.event === 'game.state.sync' || event.event === 'game.state.delta'
          ? null
          : this.stateValue.pendingCommand,
      error: null,
    });
    if (event.event === 'game.state.sync') void this.refreshActiveTakeback();
  }

  private requestSync(): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.send('game.sync.request', {}, false);
  }

  private reconcileRecoverySync(): void {
    const ownDisconnectionMarked = this.isOwnDisconnectionMarked();

    if (this.stateValue.connection !== 'online' || !ownDisconnectionMarked) {
      if (this.recoverySyncTimer !== null) clearTimeout(this.recoverySyncTimer);
      this.recoverySyncTimer = null;
      this.recoverySyncAttempt = 0;
      return;
    }
    if (this.recoverySyncTimer !== null) return;

    const delay = Math.min(2_000, 250 * 2 ** Math.min(this.recoverySyncAttempt, 3));
    this.recoverySyncTimer = setTimeout(() => {
      this.recoverySyncTimer = null;
      if (this.stateValue.connection !== 'online' || !this.isOwnDisconnectionMarked()) {
        this.recoverySyncAttempt = 0;
        return;
      }
      this.recoverySyncAttempt += 1;
      this.requestSync();
      this.reconcileRecoverySync();
    }, delay);
  }

  private isOwnDisconnectionMarked(): boolean {
    const snapshot = this.stateValue.snapshot;
    const disconnection = snapshot?.disconnection;
    if (snapshot === null || snapshot === undefined || disconnection == null) return false;
    const membershipId = this.identity.snapshot().session?.data.tokens.selectedMembershipId;
    const seat = snapshot.room.players.find((player) => player.membershipId === membershipId)?.seat;
    return (
      (seat === 'BLACK' && disconnection.blackDisconnectedAt !== null) ||
      (seat === 'WHITE' && disconnection.whiteDisconnectedAt !== null)
    );
  }

  private async refreshActiveTakeback(): Promise<void> {
    const gameId = this.stateValue.snapshot?.gameId;
    if (gameId === undefined) return;
    try {
      const response = await this.identity.authorized(
        `/games/${encodeURIComponent(gameId)}/takebacks/active`,
        gameTakebackResponseSchema,
      );
      if (this.stateValue.snapshot?.gameId !== gameId) return;
      this.acceptRestSnapshot(response.data.snapshot, response.data.takeback);
    } catch {
      // Realtime events remain the authoritative fallback when the optional projection query fails.
    }
  }

  private acceptRestSnapshot(
    snapshot: TimedGameSnapshot,
    takeback: GameTakebackSnapshot | null,
  ): void {
    const current = this.stateValue.snapshot;
    if (current === null || current.gameId !== snapshot.gameId) return;
    this.update({
      ...this.stateValue,
      snapshot: snapshot.stateVersion >= current.stateVersion ? snapshot : current,
      activeTakeback: takeback?.status === 'PENDING' ? takeback : null,
      pendingCommand: null,
      error: null,
    });
  }

  private commandFailed(error: unknown): void {
    const requiresSync = error instanceof WebApiError && error.syncRequired;
    this.update({
      ...this.stateValue,
      pendingCommand: null,
      error: errorMessage(error, '操作失败，请重试。'),
    });
    if (requiresSync) this.requestSync();
  }

  private requireSnapshot(): TimedGameSnapshot {
    const snapshot = this.stateValue.snapshot;
    if (snapshot === null) throw new Error('当前没有打开的对局。');
    return snapshot;
  }

  private update(state: GameState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
    this.reconcileRecoverySync();
  }
}
